"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import useRouter from "@stadium/hooks/lib/use-custom-route";
import ToolTip from "@stadium/ui/src/components/tooltip/tooltip";
import CountryFlag from "@stadium/ui/src/shared/country-flag";
import { useConfig } from "@stadium/hooks/lib/use-config";
import Label from "@stadium/ui/src/components/form/Label";

import { pluralizeWord } from "@stadium/helpers/string";

import { InternalButton } from "@sharedComponents/buttons";
import Multimedia from "@sharedComponents/multimedia";
import ProductColorPills from "@sharedComponents/cards/product/color-pills";
import ProductPrice from "@sharedComponents/cards/product/price";
import ProductCountryListing from "@stadium/shared/src/components/products/country-listing";
import PaginationSlider from "@stadium/ui/src/components/pagination-slider";
import TextInput from "@stadium/ui/src/components/form/TextInput";
import SelectDropdown from "@stadium/ui/src/components/form/SelectDropdown";

import useManageDraftCollection from "@stadium/shared/src/hooks/catalog/use-manage-draft-collection";
import { useStoreContext } from "@components/store/provider";
import { useCreateSwagContext } from "@contexts/create-swag-context";
import usePopulateAutomatedCatalog from "@hooks/automated-catalog/use-populate-automated-catalog";
import useFetchPaginatedDraftCatalogProducts from "@hooks/automated-catalog/use-fetch-paginated-draft-catalog-products";
import useAllCountries from "@hooks/countries/use-all-countries";
import { getCountriesGroupedByIsoUsingCountryZoneId } from "@stadium/redux-utils/selectors/configSelector";
import { useGuideTourContext } from "@contexts/guide-tour";
import { SWAG_NAME_INPUT_PROPS } from "@constants/create-swag";
import { GLOBAL_COUNTRY } from "@constants/country";

import NoImage from "@assets/images/no-image-portrait.svg";

import styles from "./main.module.scss";

const CAROUSEL_THRESHOLD = 10;

const carouselSliderSettings = {
  dots: false,
  infinite: false,
  speed: 500,
  variableWidth: true,
  slidesToScroll: 5,
  swipeToSlide: true,
  slidesToShow: 10,
};

const SwagFooterCart = () => {
  const router = useRouter();
  const {
    swagName,
    swagCollection,
    setSwagCollection,
    swagCollectionMeta,
    setSwagCollectionMeta,
    draftSwagData,
    setDraftSwagData,
    currentUserConfig,
  } = useCreateSwagContext();
  const { storeBasePath } = useStoreContext();
  const { saveDraftSwag } = useManageDraftCollection();
  const { removeProductFromAutomatedCatalog, loading: automatedCatalogLoading } =
    usePopulateAutomatedCatalog();

  const { guideTourSteps = {}, startGuideTour } = useGuideTourContext();
  const { step1 = {}, step2 = {}, step3 = {} } = guideTourSteps;

  const [selectedCountryIso, setSelectedCountryIso] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [swagNameInput, setSwagNameInput] = useState(swagName);
  const [defaultHasMore, setDefaultHasMore] = useState(false);

  const catalogNumber = draftSwagData?.number;
  const { hasMore, fetchNextPage, isFetchingFirstPage, isFetchingNextPage } =
    useFetchPaginatedDraftCatalogProducts({
      catalogNumber,
      setProducts: setSwagCollection,
      setMeta: setSwagCollectionMeta,
      countryIso: selectedCountryIso,
      defaultHasMore,
    });

  const sentinelRef = useRef(null);
  const countriesByIso = useConfig(getCountriesGroupedByIsoUsingCountryZoneId);
  const { allCountriesList } = useAllCountries();

  const productCount = Number(swagCollectionMeta?.total) || swagCollection.length;
  const isCartEmpty = productCount === 0;
  const isCarousel = swagCollection.length > CAROUSEL_THRESHOLD;
  const hasUnloadedProducts = productCount > swagCollection.length;
  const countryWiseProductCount = swagCollectionMeta?.countryWiseProductCount || {};
  const globalProductCount = Number(swagCollectionMeta?.globalProductCount) || 0;

  const countryCountEntries = useMemo(() => {
    const countryOrderByIso = new Map(
      allCountriesList.map((country, index) => [country.iso?.toUpperCase(), index]),
    );

    return Object.entries(countryWiseProductCount)
      .map(([iso, count]) => [iso?.toUpperCase(), Number(count) || 0])
      .filter(([, count]) => count > 0)
      .sort((a, b) => {
        const orderA = countryOrderByIso.get(a[0]) ?? Number.MAX_SAFE_INTEGER;
        const orderB = countryOrderByIso.get(b[0]) ?? Number.MAX_SAFE_INTEGER;
        return orderA - orderB;
      });
  }, [countryWiseProductCount, allCountriesList]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !isExpanded) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) fetchNextPage();
      },
      { rootMargin: "200px" },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [isExpanded, fetchNextPage]);

  useEffect(() => {
    setSwagNameInput(swagName);
  }, [swagName]);

  useEffect(() => {
    if (!productCount) return;
    if (!currentUserConfig?.id) return;
    if (currentUserConfig?.automatedCatalogFooterCartGuideTourViewed) return;

    setIsExpanded(true);

    const timerId = window.setTimeout(() => {
      startGuideTour();
    }, 2000);

    return () => window.clearTimeout(timerId);
  }, [
    productCount,
    currentUserConfig?.id,
    currentUserConfig?.automatedCatalogFooterCartGuideTourViewed,
    startGuideTour,
  ]);

  const handleToggleExpand = () => {
    if (isExpanded) {
      setSelectedCountryIso("");
    }
    if (!isExpanded) {
      setDefaultHasMore(swagCollectionMeta?.total > swagCollection.length);
    }
    setIsExpanded(!isExpanded);
    setIsEditingName(false);
    setSwagNameInput(swagName);
  };

  const handleRemoveProduct = async (product) => {
    if (automatedCatalogLoading) return;

    const productId = Number(product?.productId || product?.id);
    if (!Number.isFinite(productId)) return;

    const { success } = await removeProductFromAutomatedCatalog({ productId });
    if (!success) return;
  };

  const handleRemoveAllItems = async (e) => {
    e?.preventDefault?.();
    if (automatedCatalogLoading || isCartEmpty) return;

    const isGlobalSelected = selectedCountryIso === GLOBAL_COUNTRY.iso;
    const removeAllInput = {
      removeAllProducts: true,
      ...(selectedCountryIso
        ? isGlobalSelected
          ? { globalZone: true }
          : { iso: selectedCountryIso }
        : {}),
    };

    const { success } = await removeProductFromAutomatedCatalog(removeAllInput);
    if (!success) return;
    setSelectedCountryIso("");
  };

  const handleContinue = () => {
    router.push(`${storeBasePath}/add-logo`);
  };

  const getProductImage = (product) => {
    const { selectedColorsWithTemplate, variantImages, images } = product;
    return (
      selectedColorsWithTemplate?.[0]?.images?.[0] || variantImages?.[0] || images?.[0] || NoImage
    );
  };

  const getCountryCount = (countryIso) => {
    if (countryIso === GLOBAL_COUNTRY.iso) {
      return globalProductCount;
    }
    return countryWiseProductCount?.[countryIso] || 0;
  };

  const onCountrySelect = ({ value }) => {
    if (value === selectedCountryIso) {
      setSelectedCountryIso("");
      return;
    }
    setSelectedCountryIso(value);
  };

  const handleChangeSwagName = ({ value }) => {
    setSwagNameInput(value);
  };

  const onSaveSwagName = async () => {
    const trimmedName = swagNameInput.trim();
    const input = {
      name: trimmedName,
      id: draftSwagData.id,
    };

    const catalog = await saveDraftSwag(input);
    if (!catalog) return;
    setDraftSwagData((prev) => ({
      ...prev,
      name: catalog.name,
    }));
    setIsEditingName(false);
  };

  const handleSwagNameFormSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!swagNameInput?.trim()) return;
    void onSaveSwagName();
  };

  const renderCountryCountItem = ({ label, count, country, showTooltip = false }) => (
    <article key={label} className={styles.dropdownGroup}>
      {country ? (
        <CountryFlag
          country={country}
          className={styles.globalFlag}
          type="round"
          width={26}
          height={18}
          isNextImage={false}
        />
      ) : null}
      <div>
        <span className={styles.countryName}>{label}</span>
        <span className={styles.countryCountValue}>({count})</span>
      </div>

      {showTooltip && (
        <ToolTip content={label} placement="top" isLightTheme arrow>
          <span className={styles.infoIcon} />
        </ToolTip>
      )}
    </article>
  );

  const renderCountryOption = ({ iso, count }) => {
    const country = countriesByIso?.[iso];
    return {
      text: renderCountryCountItem({
        label: country?.name || iso,
        count,
        country: country || { iso, name: iso },
      }),
      countryName: country?.name || iso,
      iso,
      value: iso,
    };
  };

  const handleAllSwagClick = () => {
    setSelectedCountryIso("");
  };

  const renderCollectionCountBar = () => (
    <section className={styles.collectionCountBar}>
      <InternalButton
        styleType="no-style"
        type="button"
        className={styles.allSwagCount}
        onClick={handleAllSwagClick}
        aria-label="All swag"
      >
        All Swag ({swagCollectionMeta?.totalProductsCount})
      </InternalButton>
      <span className={styles.divider}></span>
      <div ref={step2.ref} className={styles.cartTourCountryAnchor}>
        <SelectDropdown
          options={[
            ...(globalProductCount > 0
              ? [renderCountryOption({ iso: "GLOBAL", count: globalProductCount })]
              : []),
            ...countryCountEntries.map(([iso, count]) => {
              return renderCountryOption({ iso, count });
            }),
          ]}
          showSearchFilter
          searchfilterOptions={{ attributes: ["iso", "countryName"], placeholder: "Search" }}
          onSelect={onCountrySelect}
          placeholder="view swag per country"
          value={selectedCountryIso}
          className={styles.cartDropdown}
          toggleClass={styles.dropDownBtn}
          placeholderTextClass={styles.dropDownBtnValue}
          wrapperClassName={styles.dropdownWrapper}
          inputClassName={styles.searchInput}
          menuClass={styles.cartDropdownList}
          text={
            renderCountryOption({
              iso: selectedCountryIso,
              count: getCountryCount(selectedCountryIso),
            })?.text || ""
          }
        />
      </div>
    </section>
  );
  const renderCard = (product) => {
    const image = getProductImage(product);
    const brandName = product?.brand?.displayName || product?.brand?.name || "";
    const colorOptions = (product?.selectedColorOptions || []).map((color) => ({
      ...color,
      value: color.name,
    }));
    const uniqueKey = product?.id;
    return (
      <article
        key={uniqueKey}
        className={styles.footerProductCard}
        aria-label={product?.name}
        tabIndex={0}
      >
        <div className={styles.footerProductImageWrap}>
          <Multimedia
            type="image"
            src={image}
            alt={product?.name}
            height={96}
            width={96}
            isNextImage={false}
            className={styles.footerProductImage}
          />
        </div>

        <div className={styles.footerProductContent}>
          <ProductCountryListing
            countries={product?.availableCountries}
            availableGlobally={product?.availableGlobally}
            sorting={false}
            className={styles.productCountryListing}
          />
          {brandName && <div className={styles.footerProductBrand}>{brandName}</div>}
          <section className={styles.productNameWrapper}>
            <div className={styles.footerProductName} title={product?.name}>
              {product?.name}
            </div>
            <div className={styles.footerProductPrice}>
              <ProductPrice product={product} source="search" />
            </div>
          </section>

          <ProductColorPills
            colors={colorOptions}
            totalColorsCount={colorOptions.length}
            containerClass={styles.footerCardColors}
          />
        </div>

        <InternalButton
          styleType="no-style"
          type="button"
          className={styles.trashButton}
          disabled={automatedCatalogLoading}
          onClick={async () => handleRemoveProduct(product)}
          aria-label={`Remove ${product?.name}`}
        >
          <span className={styles.trashIcon} />
        </InternalButton>
      </article>
    );
  };

  const renderAddArtworkCta = () => (
    <InternalButton
      ref={step3.ref}
      styleType="primary"
      onClick={handleContinue}
      className={styles.continueButton}
      disabled={isCartEmpty}
      aria-label="Add artwork"
    >
      Add Artwork
    </InternalButton>
  );

  const renderEditButton = () => (
    <InternalButton
      ref={step1.ref}
      styleType="link"
      onClick={() => setIsEditingName(true)}
      className={styles.editButton}
      aria-label="Edit"
    >
      <i className={styles.editIcon} />
    </InternalButton>
  );

  const renderThumbnail = (product) => {
    const image = getProductImage(product);
    return (
      <div key={product.id} className={styles.thumbnailImageWrapper}>
        <Multimedia
          type="image"
          src={image}
          alt={product.name}
          height={60}
          width={60}
          isNextImage={false}
          className={styles.thumbnailImg}
        />
        <InternalButton
          styleType="no-style"
          type="button"
          className={styles.cardCloaseButton}
          disabled={automatedCatalogLoading}
          onClick={async (e) => {
            e.stopPropagation();
            await handleRemoveProduct(product);
          }}
          aria-label={`Remove ${product.name}`}
        >
          <span className={styles.closeIcon} />
        </InternalButton>
      </div>
    );
  };

  const renderViewAllTile = () =>
    hasUnloadedProducts ? (
      <InternalButton
        key="view-all"
        styleType="no-style"
        type="button"
        className={styles.viewAllTile}
        onClick={handleToggleExpand}
        aria-label="View all products"
        innerChildClass={styles.viewAllTileInnerChild}
      >
        <span className={styles.viewAllText}>+{productCount - swagCollection.length}</span>
      </InternalButton>
    ) : null;

  const renderCollapsedView = () => (
    <div className={styles.collapsedContainer}>
      <section className={styles.leftSection}>
        <article className={styles.collectionInfo}>
          <span className={styles.collectionName}>{swagName}</span>
          {productCount ? (
            <span className={styles.productCount}>
              ({productCount} {pluralizeWord("item", { count: productCount })})
            </span>
          ) : null}
        </article>
        {renderEditButton()}
        <div className={styles.borderLeft} />
        {isCartEmpty ? (
          <div className={styles.emptyMessage}>Select products to start designing</div>
        ) : isCarousel ? (
          <PaginationSlider
            sliderSettings={carouselSliderSettings}
            className={styles.footerSlider}
            containerClass={styles.footerSliderContainer}
          >
            {swagCollection.map(renderThumbnail)}
            {renderViewAllTile()}
          </PaginationSlider>
        ) : (
          <section className={styles.thumbnailsContainer}>
            {swagCollection.map(renderThumbnail)}
            {renderViewAllTile()}
          </section>
        )}
      </section>

      <div className={styles.rightSection}>{renderAddArtworkCta()}</div>
    </div>
  );

  const renderExpandedView = () => (
    <section className={styles.expandedContainer}>
      <div className={styles.headerBlock}>
        <header
          className={`${styles.cartHeader} ${isEditingName ? styles.expandedHeader : ""}`}
          aria-labelledby="swagName"
        >
          {isEditingName ? (
            <>
              <form className={styles.inputWrapper} onSubmit={handleSwagNameFormSubmit}>
                <TextInput
                  value={swagNameInput}
                  onChange={handleChangeSwagName}
                  inputContainerClass={styles.nameInput}
                  label={<Label showIcon={false}>Collection Name</Label>}
                  {...SWAG_NAME_INPUT_PROPS}
                />
                <InternalButton
                  type="submit"
                  styleType="primary-outline"
                  className={styles.collectionSaveBtn}
                  disabled={!swagNameInput?.trim()}
                >
                  save
                </InternalButton>
              </form>
            </>
          ) : (
            <>
              <h4 className={styles.expandedCollectionName} id="swagName">
                {swagName}
              </h4>
              {renderEditButton()}
            </>
          )}
        </header>
        {!isCartEmpty && renderCollectionCountBar()}
      </div>

      <div className={`${styles.expandedBody} ${isCartEmpty ? styles.emptyBody : ""}`}>
        {!isCartEmpty && (
          <div className={styles.productsGrid}>
            {swagCollection.map(renderCard)}
            {hasMore && (
              <div ref={sentinelRef} className={styles.loadMoreSentinel}>
                {isFetchingNextPage && <span className={styles.loadingSpinner} />}
              </div>
            )}
          </div>
        )}
      </div>

      <div className={isCartEmpty ? styles.expandedFooterNoRemove : styles.expandedFooter}>
        {isCartEmpty ? (
          <div className={styles.emptyMessageExpanded}>Select products to start designing</div>
        ) : null}

        {isCartEmpty ? null : (
          <InternalButton
            styleType="link"
            className={styles.removeAllButton}
            onClick={handleRemoveAllItems}
            aria-disabled={automatedCatalogLoading}
            aria-label="Remove all items"
          >
            remove all items
          </InternalButton>
        )}

        {renderAddArtworkCta()}
      </div>
    </section>
  );

  return (
    <section
      className={`${styles.footerCart} ${isExpanded ? styles.expanded : styles.collapsed}`}
      aria-busy={automatedCatalogLoading}
    >
      <article
        className={`${styles.dataContainer} ${isCartEmpty ? styles.dataContainerEmpty : ""}`}
      >
        <InternalButton
          styleType="no-style"
          type="button"
          className={styles.footerToggleButton}
          onClick={handleToggleExpand}
          aria-label={isExpanded ? "Collapse cart" : "Expand cart"}
        >
          <span className={styles.toggleIcon} />
        </InternalButton>

        <div className={styles.cartContentWrapper}>
          <div className={styles.cartContent}>
            {renderCollapsedView()}
            {renderExpandedView()}
          </div>
          {(automatedCatalogLoading || (isFetchingFirstPage && isExpanded)) && (
            <div
              className={styles.cartLoadingOverlay}
              role="status"
              aria-live="polite"
              aria-label="Updating collection"
            >
              <span className={styles.cartLoadingSpinner} />
            </div>
          )}
        </div>
      </article>
    </section>
  );
};

export default SwagFooterCart;
